import React, { useState } from "react";
import { ApolloProvider, gql, useQuery, useMutation } from "@apollo/client";
import client from "../lib/apolloClient";
import PartnerProfilePage from "../components/Succeedo/PartnerDirectory/PartnerProfilePage";

const PARTNER_PROFILE_QUERY = gql`
  query PartnerProfile($id: ID!) {
    currentUser {
      id
      name
      succeedo_persona
      coach_profile {
        id
        is_partner
      }
      action_plan {
        open_count(source: "succeedo")
        completed_count(source: "succeedo")
      }
    }
    partner(id: $id) {
      id
      name
      org_name
      partner_category
      bio
      geography_regions
      industry_tags
      website_url
      contact_email
      accepts_introductions
      avatar_url
      rating
      verified
      intro_state
      intro_request_id
      on_my_team
      advisory_team_member_id
    }
  }
`;

const CREATE_INTRODUCTION_REQUEST = gql`
  mutation CreateIntroductionRequest($partnerCategory: String!, $coachProfileId: ID) {
    create_introduction_request(partner_category: $partnerCategory, coach_profile_id: $coachProfileId) {
      introduction_request
      errors
    }
  }
`;

const ADD_ADVISORY_TEAM_MEMBER = gql`
  mutation AddAdvisoryTeamMember(
    $role: String!, $status: String!, $name: String, $email: String,
    $firm: String, $coach_profile_id: ID
  ) {
    add_advisory_team_member(
      role: $role, status: $status, name: $name, email: $email,
      firm: $firm, coach_profile_id: $coach_profile_id
    ) {
      advisory_team_member { id status }
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ? parts[0][0] : "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

function PartnerProfileLoader({ partnerId }) {
  const { data, loading, error, refetch } = useQuery(PARTNER_PROFILE_QUERY, {
    variables: { id: partnerId },
    skip: !partnerId,
  });
  const [createIntroRequest, { loading: introLoading }] = useMutation(CREATE_INTRODUCTION_REQUEST);
  const [addTeamMember, { loading: addLoading }] = useMutation(ADD_ADVISORY_TEAM_MEMBER);
  const [addError, setAddError] = useState(null);

  const user = data?.currentUser;
  const persona = (user?.succeedo_persona || "seller").toUpperCase();
  const userName = user?.name || "";
  const userInitials = deriveInitials(userName);

  const actionPlan = user?.action_plan || {};
  const openCount = actionPlan.open_count || 0;
  const completedCount = actionPlan.completed_count || 0;
  const total = Math.max(openCount + completedCount, 5);
  const planProgress = {
    completed: completedCount,
    total,
    percentage: total > 0 ? Math.round((completedCount / total) * 100) : 0,
  };

  const partner = data?.partner || null;
  const introState = partner?.intro_state || 'none';
  const onMyTeam = !!partner?.on_my_team;

  // A partner viewing their own public profile gets an Edit affordance
  // instead of the request/add CTAs aimed at sellers. Compare on the
  // CoachProfile id (what the page is keyed on), not on the user id.
  const viewerCoachProfileId = user?.coach_profile?.id || null;
  const viewingSelf = !!(viewerCoachProfileId && partner?.id && String(viewerCoachProfileId) === String(partner.id));

  const handleRequestIntroduction = async () => {
    if (!partner) return;
    setAddError(null);
    try {
      const { data: result } = await createIntroRequest({
        variables: {
          partnerCategory: partner.partner_category,
          coachProfileId: partner.id,
        },
      });
      const errors = result?.create_introduction_request?.errors || [];
      if (errors.length > 0) {
        console.error('Introduction request errors:', errors);
        return;
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_introduction_requested', {
          partner_category: partner.partner_category,
          coach_profile_id: partner.id,
          source: 'partner_profile',
        });
      }
      refetch();
    } catch (e) {
      console.error('Failed to create introduction request', e);
    }
  };

  const handleAddToDealTeam = async () => {
    if (!partner) return;
    setAddError(null);
    try {
      const { data: result } = await addTeamMember({
        variables: {
          role: partner.partner_category,
          status: 'active',
          name: partner.name,
          email: partner.contact_email || null,
          firm: partner.org_name || null,
          coach_profile_id: partner.id,
        },
      });
      const errors = result?.add_advisory_team_member?.errors || [];
      if (errors.length > 0) {
        setAddError(errors.join(', '));
        return;
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_partner_added_to_team_post_intro', {
          coach_profile_id: partner.id,
          role: partner.partner_category,
          source: 'partner_profile',
        });
      }
      // Take the seller to their team page so the new seat is visible.
      window.location.assign('/succeedo/teams');
    } catch (e) {
      console.error('Failed to add partner to deal team', e);
      setAddError('Could not add to your deal team. Please try again.');
    }
  };

  return (
    <PartnerProfilePage
      partner={partner}
      loading={loading}
      error={error || null}
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      planProgress={planProgress}
      actionPlanBadge={openCount}
      introState={introState}
      onMyTeam={onMyTeam}
      viewingSelf={viewingSelf}
      onRequestIntroduction={handleRequestIntroduction}
      onAddToDealTeam={handleAddToDealTeam}
      introLoading={introLoading}
      addLoading={addLoading}
      addError={addError}
    />
  );
}

const SucceedoPartnerProfileContainer = (props, _railsContext) => (
  <ApolloProvider client={client}>
    <PartnerProfileLoader partnerId={props.partnerId} />
  </ApolloProvider>
);

export default SucceedoPartnerProfileContainer;
